import {Svg, Path} from 'react-native-svg';
import {useEffect, useCallback, ReactNode, Fragment} from 'react';
import {StyleSheet, Pressable, View, Text} from 'react-native';
import {IconButton} from 'interface/base/IconButton';
import {colors, fonts, vectors} from 'config/styles';
import {theme} from 'config/themes';
import {isTouch} from 'utils/platform';
import events from 'extensions/events';

export interface ChoiceProps {
  title?: string;
  buttons?: ChoiceButton[];
}

export interface ChoiceButton {
  text?: string;
  icon?: ReactNode;
  cancel?: boolean;
  disabled?: boolean;
  onPress?: (inputValue?: string) => void;
}

export function Choice(props: ChoiceProps) {
  const cancel = useCallback(() => {
    let target: ChoiceButton | undefined;
    props.buttons.forEach(button => {
      if (button.cancel)
        target = button;
    });
    if (target) {
      pressButton(target);
      return true;
    }
    return false;
  }, [props.buttons]);

  const pressButton = useCallback((button: ChoiceButton) => {
    if (button.onPress) {
      button.onPress();
    }
  }, []);

  const renderButton = useCallback((button: ChoiceButton, index: number) => (
    <Fragment key={index}>
      <IconButton
        icon={button.icon}
        title={button.text}
        text={button.text || ''}
        buttonStyle={[styles.button, isTouch() && styles.buttonLarge]}
        buttonHoverStyle={styles.buttonHover}
        textStyle={styles.buttonText}
        textHoverStyle={styles.buttonTextHover}
        disabled={button.disabled}
        onPress={e => {
          e.stopPropagation();
          pressButton(button);
        }}
      />
    </Fragment>
  ), []);

  useEffect(() => events.addKeyListener('up', (e) => {
    if (e.key === 'Escape') cancel();
  }).unsubscribe, [props.buttons]);

  return (
    <View style={styles.root}>
      <View importantForAccessibility="yes">
        <Text style={styles.title}>
          {props.title}
        </Text>
      </View>
      <Pressable onPress={cancel} style={styles.close}>
        <Svg viewBox="0 0 14 14" width={14} height={14}>
          <Path d={vectors.icons.close} fill={colors.neutral.mid}/>
        </Svg>
      </Pressable>
        {props.buttons && props.buttons.length > 0 &&
          <View style={styles.buttons}>
            {props.buttons.map(renderButton)}
          </View>
        }
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: theme.dialog.fill,
  },
  title: {
    fontSize: fonts.sizes.xl,
    color: theme.dialog.title,
    padding: 20,
  },
  container: {
    justifyContent: 'center',
  },
  buttons: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    paddingHorizontal: 12,
    paddingBottom: 20,
  },
  button: {
    minHeight: 42,
    paddingHorizontal: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  buttonLarge: {
    minHeight: 54,
    marginBottom: 4,
  },
  buttonHover: {
    backgroundColor: theme.dialog.hover,
  },
  buttonText: {
    fontSize: fonts.sizes.m,
    color: theme.menu.text,
  },
  buttonTextHover: {
    color: colors.neutral.white,
  },
  close: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 10,
    marginRight: 6,
    padding: 8,
    height: 46,
    width: 46,
    right: 0,
  },
});
